import { getApiClient } from '@src/shared/types/api-client'
import type {
  ContainerSummary,
  FieldConfigResponse,
  FieldConfigSaveResponse,
  FieldConfigUpdate,
} from '@src/tools/anonymizer/types/Constants'
import { MutationFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { anonymizerKeys } from './query-keys'

export function useContainers(dbName: string | null): [ContainerSummary[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: anonymizerKeys.containers(dbName!),
    queryFn: async () => {
      const apiClient = getApiClient()
      const response = await apiClient.get<ContainerSummary[]>(`field-configs/${encodeURIComponent(dbName!)}`)
      return response.data
    },
    enabled: !!dbName,
    placeholderData: [],
  })
  return [data ?? [], isFetching]
}

export function useFieldConfigs(
  dbName: string | null,
  containerName: string | null,
): [FieldConfigResponse[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: anonymizerKeys.fieldConfigs(dbName!, containerName!),
    queryFn: async () => {
      const apiClient = getApiClient()
      const response = await apiClient.get<FieldConfigResponse[]>(
        `field-configs/${encodeURIComponent(dbName!)}/${encodeURIComponent(containerName!)}`,
      )
      return response.data
    },
    enabled: !!dbName && !!containerName,
    placeholderData: [],
  })
  return [data ?? [], isFetching]
}

const updateFieldConfigs: MutationFunction<
  FieldConfigSaveResponse,
  { dbName: string; containerName: string; updates: FieldConfigUpdate[] }
> = async ({ dbName, containerName, updates }) => {
  const apiClient = getApiClient()
  const response = await apiClient.put<FieldConfigSaveResponse>(
    `field-configs/${encodeURIComponent(dbName)}/${encodeURIComponent(containerName)}`,
    updates,
  )
  return response.data
}

export function useUpdateFieldConfigs(): [
  MutationFunction<
    FieldConfigSaveResponse,
    { dbName: string; containerName: string; updates: FieldConfigUpdate[] }
  >,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: updateFieldConfigs,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: anonymizerKeys.allFieldConfigs() })
      await queryClient.invalidateQueries({ queryKey: anonymizerKeys.allContainers() })
      await queryClient.invalidateQueries({ queryKey: anonymizerKeys.databasePendingApprovalCounts() })
      await queryClient.invalidateQueries({ queryKey: anonymizerKeys.containerStatuses() })
    },
  })
  return [mutateAsync, isPending, isError, reset]
}
